import { useState, useRef, useEffect } from 'react'
import { useAuth } from '../lib/auth'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface OnboardingPageProps {
  onComplete?: () => void
}

type Step = 'welcome' | 'phone' | 'enroll' | 'complete'

export function OnboardingPage({ onComplete }: OnboardingPageProps) {
  const { getToken } = useAuth()
  const [step, setStep] = useState<Step>('welcome')
  const [userName, setUserName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [savingPhone, setSavingPhone] = useState(false)

  const handleEnrollmentComplete = () => {
    setStep('complete')
    if (onComplete) {
      setTimeout(onComplete, 2000)
    }
  }

  async function handlePhoneContinue() {
    if (!phoneNumber.trim()) {
      // Skip if empty
      setStep('enroll')
      return
    }

    // Validate format
    const phone = phoneNumber.trim()
    if (!phone.startsWith('+')) {
      setPhoneError('Phone number must start with + and include country code (e.g., +44)')
      return
    }

    setSavingPhone(true)
    setPhoneError(null)

    try {
      const token = await getToken()
      const res = await fetch(`${API_BASE}/v1/users/me/phone`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone_number: phone }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Failed to save phone number')
      }

      setStep('enroll')
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Failed to save phone number')
    } finally {
      setSavingPhone(false)
    }
  }

  return (
    <div className="onboarding-wrapper">
      <div className="onboarding-card">
        {step === 'welcome' && (
          <>
            <div className="onboarding-icon">
              <WaveIcon />
            </div>
            <h1 className="onboarding-title">Welcome to cloopOS</h1>
            <p className="onboarding-description">
              Let's set up your voice profile so we can identify you in conversations.
            </p>

            <div style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
              <label className="input-label">What should we call you?</label>
              <input
                type="text"
                className="input"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Your name"
                autoFocus
              />
            </div>

            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%' }}
              onClick={() => setStep('phone')}
              disabled={!userName.trim()}
            >
              Continue
            </button>
          </>
        )}

        {step === 'phone' && (
          <>
            <div className="onboarding-icon">
              <PhoneIcon />
            </div>
            <h1 className="onboarding-title">Connect Your Phone</h1>
            <p className="onboarding-description">
              Add your phone number to capture calls via WhatsApp. You can skip this for now.
            </p>

            <div style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
              <label className="input-label">Phone Number (optional)</label>
              <input
                type="tel"
                className="input"
                value={phoneNumber}
                onChange={(e) => { setPhoneNumber(e.target.value); setPhoneError(null) }}
                placeholder="+44 7911 123456"
              />
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-dim)', marginTop: '0.5rem' }}>
                Used to identify you when you call cloopOS via WhatsApp
              </p>
              {phoneError && (
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-rose)', marginTop: '0.5rem' }}>
                  {phoneError}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                className="btn btn-secondary btn-lg"
                style={{ flex: 1 }}
                onClick={() => setStep('enroll')}
                disabled={savingPhone}
              >
                Skip
              </button>
              <button
                className="btn btn-primary btn-lg"
                style={{ flex: 1 }}
                onClick={handlePhoneContinue}
                disabled={savingPhone}
              >
                {savingPhone ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </>
        )}

        {step === 'enroll' && (
          <VoiceEnrollment
            speakerName={userName}
            onComplete={handleEnrollmentComplete}
            onBack={() => setStep('phone')}
          />
        )}

        {step === 'complete' && (
          <>
            <div className="onboarding-icon onboarding-icon-success">
              <CheckIcon />
            </div>
            <h1 className="onboarding-title">You're all set!</h1>
            <p className="onboarding-description">
              Your voice has been enrolled. You can now start capturing conversations.
            </p>
            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%' }}
              onClick={onComplete}
            >
              Get Started
            </button>
          </>
        )}

        {/* Step indicator */}
        <div className="onboarding-steps">
          <div className={`onboarding-step ${step === 'welcome' ? 'active' : 'complete'}`} />
          <div className={`onboarding-step ${step === 'phone' ? 'active' : step === 'enroll' || step === 'complete' ? 'complete' : ''}`} />
          <div className={`onboarding-step ${step === 'enroll' ? 'active' : step === 'complete' ? 'complete' : ''}`} />
          <div className={`onboarding-step ${step === 'complete' ? 'active complete' : ''}`} />
        </div>
      </div>
    </div>
  )
}

// Voice Enrollment Component
function VoiceEnrollment({
  speakerName,
  onComplete,
  onBack
}: {
  speakerName: string
  onComplete: () => void
  onBack: () => void
}) {
  const { getToken } = useAuth()
  const [state, setState] = useState<'idle' | 'recording' | 'uploading'>('idle')
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const MIN_DURATION = 30
  const MAX_DURATION = 60

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
      }
    }
  }, [])

  async function startRecording() {
    setError(null)
    chunksRef.current = []

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      })
      streamRef.current = stream

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : 'audio/webm'

      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => uploadEnrollment()

      recorder.start(1000)
      setState('recording')

      timerRef.current = setInterval(() => {
        setDuration(d => {
          if (d >= MAX_DURATION - 1) {
            stopRecording()
            return d + 1
          }
          return d + 1
        })
      }, 1000)
    } catch {
      setError('Could not access microphone. Please allow microphone access.')
    }
  }

  function stopRecording() {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop()
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
  }

  async function uploadEnrollment() {
    if (chunksRef.current.length === 0) return
    setState('uploading')

    try {
      const token = await getToken()
      const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm'
      const blob = new Blob(chunksRef.current, { type: mimeType })

      const formData = new FormData()
      formData.append('audio', blob, 'enrollment.webm')
      formData.append('name', speakerName)
      formData.append('is_primary', 'true')

      const res = await fetch(`${API_BASE}/v1/speakers/enroll`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Enrollment failed')
      }

      onComplete()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enrollment failed. Please try again.')
      setState('idle')
      setDuration(0)
    }
  }

  const progress = Math.min((duration / MIN_DURATION) * 100, 100)
  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

  return (
    <>
      <div className="onboarding-icon">
        <MicIcon />
      </div>
      <h1 className="onboarding-title">Enroll Your Voice</h1>
      <p className="onboarding-description">
        Record 30-60 seconds of you speaking naturally. Read aloud, count, or just talk about your day.
      </p>

      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      {/* Progress bar */}
      <div className="progress-container">
        <div className="progress-label">
          <span className="progress-label-text">
            {state === 'idle' && 'Ready to record'}
            {state === 'recording' && 'Recording...'}
            {state === 'uploading' && 'Processing...'}
          </span>
          <span className="progress-label-value">{formatTime(duration)} / 0:30 min</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Visualizer */}
      <div className="recorder-visualizer">
        {Array.from({ length: 28 }).map((_, i) => (
          <div
            key={i}
            className={`recorder-bar ${state === 'recording' ? 'active' : ''}`}
            style={{ animationDelay: `${i * 0.04}s` }}
          />
        ))}
      </div>

      {/* Instructions */}
      <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.9375rem' }}>
        {state === 'idle' && 'Click the button below to start recording'}
        {state === 'recording' && duration < MIN_DURATION && `Keep speaking for ${MIN_DURATION - duration} more seconds...`}
        {state === 'recording' && duration >= MIN_DURATION && 'Great! You can stop recording now'}
        {state === 'uploading' && 'Analyzing your voice pattern...'}
      </p>

      {/* Controls */}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        {state === 'idle' && (
          <>
            <button className="btn btn-secondary btn-lg" style={{ flex: '0 0 auto' }} onClick={onBack}>
              Back
            </button>
            <button className="btn btn-primary btn-lg" style={{ flex: 1 }} onClick={startRecording}>
              <MicIcon /> Start Recording
            </button>
          </>
        )}
        {state === 'recording' && (
          <button
            className="btn btn-primary btn-lg"
            style={{ width: '100%', background: duration < MIN_DURATION ? 'var(--color-amber)' : undefined }}
            disabled={duration < MIN_DURATION}
            onClick={stopRecording}
          >
            {duration < MIN_DURATION ? (
              <>Recording ({MIN_DURATION - duration}s left)</>
            ) : (
              <>Stop & Save</>
            )}
          </button>
        )}
        {state === 'uploading' && (
          <button className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled>
            <span style={{ width: 16, height: 16, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', display: 'inline-block' }} />
            Processing...
          </button>
        )}
      </div>
    </>
  )
}

// Icons
function WaveIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

function MicIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" x2="12" y1="19" y2="22" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}
